/**
 * API Client
 *
 * Axios-based client for communicating with the Cloudflare Workers API
 */

import axios from 'axios';
import type {
  PromptToPlanRequest,
  PlanToLumaScriptRequest,
  LumaScriptToBPMNRequest,
  LumaScript,
  Skill,
  ExportRequest,
  ExportResponse
} from '@luma-factory/shared';
import { buildUserHeaders, getActiveUserId } from '@/stores/userStore';
import { API_BASE_URL } from '@/lib/config';

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const userId = getActiveUserId() || 'default-user';
  config.headers = {
    ...(config.headers || {}),
    'X-Luma-User': userId
  };
  return config;
});

/**
 * Transform API
 */
export const transformApi = {
  /**
   * Transform prompt to plan (streaming)
   */
  async promptToPlan(request: PromptToPlanRequest): Promise<ReadableStream<Uint8Array>> {
    const response = await fetch(`${API_BASE_URL}/transforms/prompt-to-plan`, {
      method: 'POST',
      headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`Transform failed: ${response.statusText}`);
    }

    return response.body!;
  },

  /**
   * Transform plan to lumascript (streaming)
   */
  async planToLumaScript(request: PlanToLumaScriptRequest): Promise<ReadableStream<Uint8Array>> {
    const response = await fetch(`${API_BASE_URL}/transforms/plan-to-lumascript`, {
      method: 'POST',
      headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error(`Transform failed: ${response.statusText}`);
    }

    return response.body!;
  },

  /**
   * Transform lumascript to BPMN using AI with structured output (async via queue)
   * Returns job ID for polling
   */
  async lumaScriptToBPMN(request: LumaScriptToBPMNRequest & { skillId: string }): Promise<{ jobId: string; status: string; message: string }> {
    const response = await api.post('/transforms/lumascript-to-bpmn', request);
    return response.data;
  },

  /**
   * Poll job status for async BPMN generation
   */
  async getJobStatus(jobId: string): Promise<{
    id: string;
    skillId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    result?: string;
    error?: string;
    createdAt: number;
    startedAt?: number;
    completedAt?: number;
  }> {
    const response = await api.get(`/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Reverse: BPMN to lumascript
   */
  async bpmnToLumaScript(bpmn: string) {
    const response = await api.post('/transforms/bpmn-to-lumascript', { bpmn });
    return response.data;
  },

  /**
   * Reverse: LumaScript to plan
   */
  async lumaScriptToPlan(lumascript: LumaScript): Promise<ReadableStream<Uint8Array>> {
    const response = await fetch(`${API_BASE_URL}/transforms/lumascript-to-plan`, {
      method: 'POST',
      headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ lumascript })
    });

    if (!response.ok) {
      throw new Error(`Transform failed: ${response.statusText}`);
    }

    return response.body!;
  },

  /**
   * Plan to prompt (not implemented - reverse transformation not supported)
   * Plans are structured documents that can't be reliably reversed back to prompts
   */
  async planToPrompt(_plan: string): Promise<ReadableStream<Uint8Array>> {
    throw new Error('Plan-to-Prompt transformation is not supported. Please edit the prompt directly.');
  },

  /**
   * Transform LumaScript to Artifact Design (streaming)
   * Analyzes LumaScript and proposes artifacts for generation
   */
  async lumaScriptToArtifactDesign(lumascript: LumaScript, projectSuffix: string): Promise<ReadableStream<Uint8Array>> {
    const response = await fetch(`${API_BASE_URL}/transforms/lumascript-to-artifact-design`, {
      method: 'POST',
      headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
      credentials: 'include',
      body: JSON.stringify({ lumascript, projectSuffix })
    });

    if (!response.ok) {
      throw new Error(`Transform failed: ${response.statusText}`);
    }

    return response.body!;
  },

  /**
   * Generate selected artifacts from Artifact Design
   * Creates job and enqueues for asynchronous generation
   */
  async generateArtifacts(projectId: string, artifactId: string, selectedArtifactIds: string[]) {
    const response = await api.post('/transforms/generate-artifacts', {
      projectId,
      artifactId,
      selectedArtifactIds
    });
    return response.data;
  },

  /**
   * Poll status of artifact generation job
   */
  async getArtifactGenerationJobStatus(jobId: string) {
    const response = await api.get(`/transforms/artifact-generation-jobs/${jobId}`);
    return response.data;
  },

  /**
   * Get all generated artifacts for a project artifact
   */
  async getGeneratedArtifacts(artifactId: string) {
    const response = await api.get(`/transforms/generated-artifacts/${artifactId}`);
    return response.data.artifacts;
  }
};

/**
 * Skills API
 */
export const skillsApi = {
  /**
   * List all skills
   */
  async list() {
    const response = await api.get('/skills');
    return response.data.skills as Skill[];
  },

  /**
   * Get a skill by ID
   */
  async get(id: string) {
    const response = await api.get(`/skills/${id}`);
    return response.data;
  },

  /**
   * Create a new skill
   */
  async create(data: { name: string; facet?: string }) {
    const response = await api.post('/skills', data);
    return response.data;
  },

  /**
   * Update a skill
   */
  async update(id: string, data: {
    name?: string;
    prompt?: string;
    plan?: string;
    lumascript?: LumaScript;
    bpmn?: string;
  }) {
    const response = await api.put(`/skills/${id}`, data);
    return response.data;
  },

  /**
   * Delete a skill
   */
  async delete(id: string) {
    const response = await api.delete(`/skills/${id}`);
    return response.data;
  }
};

/**
 * Export API
 */
export const exportApi = {
  /**
   * Export skill to Luma ZIP
   */
  async exportToLumaZip(request: ExportRequest): Promise<ExportResponse> {
    const response = await api.post('/export/luma-zip', request);
    return response.data;
  },

  /**
   * List export artifacts for a skill
   */
  async listArtifacts(skillId: string) {
    const response = await api.get(`/export/artifacts/${skillId}`);
    return response.data.artifacts;
  }
};

/**
 * Stream reader helper
 */
export async function* readStream(stream: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const text = decoder.decode(value, { stream: true });
      yield text;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Integration API
 */
export const integrationApi = {
  async generateFromPlan(projectId: string, artifactId: string, plan: string) {
    const response = await api.post(`/projects/${projectId}/artifacts/${artifactId}/generate-integration`, { plan });
    return response.data as { metadata: Record<string, any>; lumaJson: Record<string, any>; warnings?: string[] };
  },
  async updateMetadata(projectId: string, artifactId: string, metadata: Record<string, any>) {
    const response = await api.put(`/projects/${projectId}/artifacts/${artifactId}/integration-metadata`, { metadata });
    return response.data as { metadata: Record<string, any>; lumaJson: Record<string, any>; warnings?: string[] };
  }
};

export async function rotateProjectSuffix(projectId: string) {
  const response = await fetch(`${API_BASE_URL}/projects/${projectId}/rotate-suffix`, {
    method: 'POST',
    headers: buildUserHeaders()
  });

  if (!response.ok) {
    throw new Error('Failed to rotate project suffix');
  }

  return response.json() as Promise<{ success: boolean; suffix: string }>;
}

// ============================================================================
// Admin Settings API
// ============================================================================

export interface SystemSetting {
  key: string;
  value: string | null;
  description: string;
  isEncrypted: boolean;
  isSensitive: boolean;
  updatedBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface ProviderModel {
  id: string;
  name: string;
  contextWindow?: number;
  factoryCompatible?: boolean;
  reasoning?: boolean;
  tool_call?: boolean;
  has_vision?: boolean;
  has_text?: boolean;
}

export const adminSettings = {
  async getAll() {
    const response = await api.get('/admin/settings');
    return response.data as { settings: SystemSetting[] };
  },

  async get(key: string) {
    const response = await api.get(`/admin/settings/${key}`);
    return response.data as { setting: SystemSetting };
  },

  async update(key: string, value: string) {
    const response = await api.put(`/admin/settings/${key}`, { value });
    return response.data as { success: boolean; setting: SystemSetting };
  },

  async clear(key: string) {
    const response = await api.delete(`/admin/settings/${key}/value`);
    return response.data as { success: boolean };
  },

  async fetchModels(
    provider: 'openai' | 'anthropic',
    options?: { apiKey?: string }
  ) {
    if (options?.apiKey) {
      const response = await api.post(`/admin/settings/models/${provider}`, { apiKey: options.apiKey });
      return response.data.models as ProviderModel[];
    }

    const response = await api.get(`/admin/settings/models/${provider}`);
    return response.data.models as ProviderModel[];
  }
};
